import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogActions, TextField, Button, Box, Typography, Tooltip } from "@mui/material";
import {getCoverWidthMm, getCoverHeightMm, getSpineMm} from "../utils/coverGeometry";


const API_URL = process.env.REACT_APP_API_URL;

interface CreateCoverAlbumModalProps {
    open: boolean;
    onClose: () => void;
    album: any;
    token: string;
    onCreated: (newAlbum: any) => void;
    defaultType?: "album" | "scase";
}

export default function CreateCoverAlbumModal({
    open,
    onClose,
    album,
    token,
    onCreated,
    defaultType,
}: CreateCoverAlbumModalProps) {
   
    const [coverType, setCoverType] = useState<"album" | "scase">("album");
    const [coverName, setCoverName] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [paperType, setPaperType] = useState<"matte" | "lustre">("matte");
    const [isCreating, setIsCreating] = useState(false);
    const canHaveSlipcase = album?.albumType === "LIFESTYLE";

    const COVER_OPTIONS = [
        {
            type: "album",
            label: "Album Cover",
            img: `${process.env.PUBLIC_URL}/cover_icon.png`,
        },
        {
            type: "scase",
            label: "Slip Case Cover",
            img: `${process.env.PUBLIC_URL}/case_icon.png`,
        },
    ] as const;

    useEffect(() => {
        if (open && album) {
            if (!canHaveSlipcase) {
                setCoverType("album"); // force album
            } else {
                setCoverType(defaultType ?? "album");
            }
        }
    }, [open, album, defaultType, canHaveSlipcase]);


    useEffect(() => {
        if (open && album) {
            const suffix = coverType === "album" ? "AC" : "ASC";
            setCoverName(`${album.albumName}_${suffix}`);
        }
    }, [open, album, coverType]);

   
    if (!album) return null;



    const handleCreate = async () => {
        if (isCreating) return;
        setIsCreating(true);

        try {
            if (!album) return;
            setError(null);
         
            const COVER_TEMPLATES: Record<string, {
                cartonW: number;
                cartonH: number;
            }> = {
                //Square
                "16x8": { cartonW: 19.61, cartonH: 21.1 },
                "20x10": { cartonW: 24.51, cartonH: 26.1 },
                "24x12": { cartonW: 29.51, cartonH: 31.21 },
                //vertical
                "22x14": { cartonW: 27.01, cartonH: 36.20 },
                "20x14": { cartonW: 24.51, cartonH: 36.20 },
                "18x12": { cartonW: 21.81, cartonH: 31.21 },
                "16x12": { cartonW: 19.61, cartonH: 31.21 },
                "16x10": { cartonW: 19.61, cartonH: 26.01 },
                "14x10": { cartonW: 17.01, cartonH: 26.01 },
                //horizontal
                "28x11": { cartonW: 34.51, cartonH: 28.71 },
                "28x10": { cartonW: 34.51, cartonH: 26.01 },
                "24x9": { cartonW: 29.51, cartonH: 23.71 },
                "24x8": { cartonW: 29.51, cartonH: 21.21 },
                "20x8": { cartonW: 24.51, cartonH: 21.01 },
                "20x7": { cartonW: 24.51, cartonH: 18.51 },
            };

            const SLIPCASE_TEMPLATES: Record<string, {
                width: number;
                height: number;
            }> = {
                //Square
                "20x10": { width: 65.7, height: 33.8 },
                 //vertical
                "20x14": { width: 65.7, height: 43.9 },
                "18x12": { width: 60.3, height: 38.9 },
            };

            const key = `${Number(album.albumSize.width)}x${Number(album.albumSize.height)}`;
            let body: any;
         
            //  COVER (ALBUM COVER)
            if (coverType === "album") {
                const template = COVER_TEMPLATES[key];
                if (!template) throw new Error(`No cover template for size ${key}`);
                const spreads = album.pageCount;
                const spineMm = getSpineMm(spreads, paperType);

                const cartonWmm = template.cartonW * 10;
                const cartonHmm = template.cartonH * 10;

                const rawCoverWmm = getCoverWidthMm({
                    spreads,
                    paperType,
                    cartonWmm,
                });

                const rawCoverHmm = getCoverHeightMm(cartonHmm);
                const toInches = (mm: number) => mm / 25.4;

                body = {
                    baseAlbumId: album._id,
                    coverType,
                    coverName,
                    albumType: "AC",
                    coverSize: {
                        width: Number(toInches(rawCoverWmm).toFixed(2)),
                        height: Number(toInches(rawCoverHmm).toFixed(2)),
                        unit: "inches",
                    },

                    backgroundColor: album.backgroundColor || "#ffffff",
                    spineWidth: spineMm
                };
            }

            //  SLIPCASE (FIXED TEMPLATE)
            const SLIPCASE_BASE_10 = {
                matte: 43,
                lustre: 45,
            };

            const SLIPCASE_INCREMENT = {
                matte: 1.0,
                lustre: 1.5,
            };
           
            if (coverType === "scase") {
                const template = SLIPCASE_TEMPLATES[key];
                if (!template) throw new Error(`No slipcase template for size ${key}`);

                const spreads = album.pageCount;

                const baseSpineMm = SLIPCASE_BASE_10[paperType];
                const perSpreadMm = SLIPCASE_INCREMENT[paperType];

                const extraSpreads = Math.max(0, spreads - 10);
                const spineMm = baseSpineMm + extraSpreads * perSpreadMm;

                //  DO NOT reconstruct fixed parts
                const finalWidthMm = template.width * 10 + (spineMm - baseSpineMm);
                const finalHeightMm = template.height * 10;

                const toInches = (mm: number) => mm / 25.4;

                body = {
                    baseAlbumId: album._id,
                    coverType,
                    coverName,
                    albumType: "ASC",
                    coverSize: {
                        width: Number(toInches(finalWidthMm).toFixed(2)),
                        height: Number(toInches(finalHeightMm).toFixed(2)),
                        unit: "inches",
                    },
                    backgroundColor: album.backgroundColor || "#ffffff",
                    spineWidth: spineMm,
                };
            }

            // 3. SERVER CALLS (UNCHANGED)

            const checkRes = await fetch(
                `${API_URL}/albums/check-name?name=${encodeURIComponent(coverName)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (!checkRes.ok) throw new Error("Failed to validate album name");

            const checkData = await checkRes.json();

            if (checkData.exists) {
                setError("An album with this name already exists");
                return;
            }

            const res = await fetch(`${API_URL}/albums/createCoverAlbum`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(body),
            });

            const data = await res.json();

            if (!res.ok) throw new Error(data.message || "Cover creation failed");

            onClose();
            onCreated(data);

        } catch (err) {
            console.error("Error creating cover:", err);
            setError((err as Error).message);

        } finally {
            setIsCreating(false);
        }
    };

  

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    transform: "scale(0.9)",
                    transformOrigin: "top center",
                    mt: "-20px",
                },
            }}
        >
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2}>
                    <Typography variant="h6">Create Album Cover / SlipCase</Typography>

                    <Typography variant="body2" color="textSecondary">
                        Based on: <strong>{album.albumName}</strong> (
                        {album.albumSize.width} × {album.albumSize.height}{" "}
                        {album.albumSize.unit})
                    </Typography>

                    <TextField
                        label="Cover Name"
                        value={coverName}
                        onChange={(e) => setCoverName(e.target.value)}
                        fullWidth
                        size="small"
                    />

                    <Box display="flex" gap={2}>
                        <Button
                            variant={paperType === "matte" ? "contained" : "outlined"}
                            onClick={() => setPaperType("matte")}
                        >
                            Matte
                        </Button>

                        <Button
                            variant={paperType === "lustre" ? "contained" : "outlined"}
                            onClick={() => setPaperType("lustre")}
                        >
                            Lustre
                        </Button>
                    </Box>

                    {/* Image selection buttons */}
                    <Box display="flex" justifyContent="space-evenly" mt={2}>
                        {COVER_OPTIONS.map(({ type, label, img }) => {
                            const disabled = type === "scase" && !canHaveSlipcase;

                            return (
                                <Tooltip
                                    key={type}
                                    title={disabled ? "Case Cover is only available for PO albums" : ""}
                                >
                                    <span>
                                        <Box
                                            onClick={() => !disabled && setCoverType(type)}

                                            sx={{
                                                width: 130,
                                                height: 130,
                                                borderRadius: 3,
                                                border: coverType === type
                                                    ? disabled
                                                        ? "2px dashed #ccc" // visually disabled selected
                                                        : "2px solid #5ab1dc"
                                                    : disabled
                                                        ? "2px solid #ccc" // visually disabled unselected
                                                        : "2px solid #ccc",
                                                backgroundColor: disabled ? "#f5f5f5" : "white",
                                                boxShadow:
                                                    coverType === type && !disabled
                                                        ? "0px 0px 8px rgba(25,118,210,0.4)"
                                                        : "0px 2px 4px rgba(0,0,0,0.1)",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                cursor: disabled ? "not-allowed" : "pointer",
                                                pointerEvents: disabled ? "none" : "auto",
                                                transition: "all 0.25s ease",
                                                "&:hover": disabled
                                                    ? {}
                                                    : {
                                                        transform: "scale(1.04)",
                                                        boxShadow: "0px 0px 10px rgba(23 135 225 / 0.3)",
                                                    },
                                                filter: disabled ? "grayscale(80%)" : "none", // grey out image
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 72,
                                                    height: 58,
                                                    borderRadius: 2,
                                                    backgroundColor: "white",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    overflow: "hidden",
                                                    mb: 1,
                                                }}
                                            >
                                                <img
                                                    src={img}
                                                    alt={label}
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "contain",
                                                        opacity: disabled ? 0.5 : 1, // grey out image
                                                    }}
                                                />
                                            </Box>

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: disabled
                                                        ? "#999" // greyed out text
                                                        : coverType === type
                                                            ? "#9f9f9f"
                                                            : "text.primary",
                                                    fontWeight: coverType === type ? 700 : 500,
                                                    textAlign: "center",
                                                }}
                                            >
                                                {label}
                                            </Typography>
                                        </Box>
                                    </span>
                                </Tooltip>
                            );
                        })}
                    </Box>

                    {error && (
                        <Typography color="error" variant="body2">
                            {error}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
         
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleCreate} disabled={isCreating}>
                    {isCreating
                        ? "Creating..."
                        : `Create ${coverType === "album" ? "Album Cover" : "Slipcase"}`
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
}
